import React, { useEffect, useRef } from "react";

interface Star {
  x: number;
  y: number;
  z: number;
  baseZ: number;
  size: number;
  color: string;
  twinkleSpeed: number;
  twinklePhase: number;
  hasSpike?: boolean;
}

interface DustCloud {
  x: number;
  y: number;
  radius: number;
  color: string;
  alpha: number;
  driftAngle: number;
  driftSpeed: number;
}

const STAR_PALETTE = [
  "rgba(255, 255, 255, ",   // 钻石纯白
  "rgba(255, 230, 180, ",   // 暖金恒星
  "rgba(255, 185, 110, ",   // 琥珀金星
  "rgba(255, 130, 45, ",    // 极光橙主序星
  "rgba(215, 240, 255, ",   // 冰蓝超巨星
];

/**
 * 顶级 3D 深空宇宙星系模拟引擎（电影级景深、十字衍射星辉、星际尘埃云、3D 鼠标引力矩阵）
 */
export const CosmicCanvas: React.FC<{ className?: string }> = ({ className = "" }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    let width = 0;
    let height = 0;
    let dpr = 1;

    // 鼠标 3D 旋转与视差追踪（带平滑阻尼惯性）
    const mouse = { x: 0, y: 0, targetX: 0, targetY: 0 };

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const nx = (e.clientX - rect.left) / width - 0.5; // -0.5 ~ 0.5
      const ny = (e.clientY - rect.top) / height - 0.5;
      mouse.targetX = nx;
      mouse.targetY = ny;
    };

    const handleMouseLeave = () => {
      mouse.targetX = 0;
      mouse.targetY = 0;
    };

    window.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseleave", handleMouseLeave);

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = canvas.parentElement?.clientWidth || window.innerWidth;
      height = canvas.parentElement?.clientHeight || window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.scale(dpr, dpr);
    };

    resize();
    window.addEventListener("resize", resize);

    // 初始化 3D 恒星群 (260 颗多光谱 3D 恒星，四级层次)
    const STAR_COUNT = 260;
    const stars: Star[] = [];
    for (let i = 0; i < STAR_COUNT; i++) {
      const z = Math.random() * 950 + 60;
      const tierRand = Math.random();
      
      let size: number;
      let hasSpike = false;

      if (tierRand < 0.12) {
        // Tier 1: 璀璨大巨星 (12%) - 晶莹十字衍射星芒
        size = Math.random() * 0.6 + 1.6;
        hasSpike = true;
      } else if (tierRand < 0.40) {
        // Tier 2: 明亮主序星 (28%) - 饱满微光 Halo
        size = Math.random() * 0.5 + 1.1;
      } else if (tierRand < 0.75) {
        // Tier 3: 标准恒星 (35%)
        size = Math.random() * 0.35 + 0.65;
      } else {
        // Tier 4: 深空远景星尘 (25%)
        size = Math.random() * 0.25 + 0.35;
      }

      stars.push({
        x: (Math.random() - 0.5) * 2600,
        y: (Math.random() - 0.5) * 1800,
        z,
        baseZ: z,
        size,
        color: STAR_PALETTE[Math.floor(Math.random() * STAR_PALETTE.length)],
        twinkleSpeed: Math.random() * 0.03 + 0.008,
        twinklePhase: Math.random() * Math.PI * 2,
        hasSpike,
      });
    }

    // 初始化深空星际尘埃云 (5 团，动态涌动)
    const dustClouds: DustCloud[] = [
      { x: width * 0.18, y: height * 0.32, radius: 280, color: "249, 115, 22", alpha: 0.042, driftAngle: 0.2, driftSpeed: 0.0003 },
      { x: width * 0.82, y: height * 0.22, radius: 340, color: "253, 186, 116", alpha: 0.035, driftAngle: 1.1, driftSpeed: 0.0002 },
      { x: width * 0.72, y: height * 0.68, radius: 300, color: "249, 115, 22", alpha: 0.038, driftAngle: 2.3, driftSpeed: 0.00025 },
      { x: width * 0.12, y: height * 0.78, radius: 260, color: "180, 83, 9", alpha: 0.032, driftAngle: 3.5, driftSpeed: 0.0002 },
      { x: width * 0.5, y: height * 0.15, radius: 240, color: "255, 140, 50", alpha: 0.028, driftAngle: 4.8, driftSpeed: 0.0002 },
    ];

    let time = 0;

    // 主渲染循环 (60/120fps 硬件加速)
    const render = () => {
      time += 1;

      // 鼠标平滑阻尼视差插值
      mouse.x += (mouse.targetX - mouse.x) * 0.05;
      mouse.y += (mouse.targetY - mouse.y) * 0.05;

      ctx.clearRect(0, 0, width, height);

      const cx = width / 2;
      const cy = height / 2;
      const fov = 460;

      // 1. 绘制深空翻涌涌动的微光星云雾 (Cosmic Nebula Clouds)
      dustClouds.forEach((cloud) => {
        cloud.driftAngle += cloud.driftSpeed;
        const ox = cloud.x + Math.cos(cloud.driftAngle) * 36 - mouse.x * 45;
        const oy = cloud.y + Math.sin(cloud.driftAngle) * 26 - mouse.y * 35;

        const grad = ctx.createRadialGradient(ox, oy, 0, ox, oy, cloud.radius);
        grad.addColorStop(0, `rgba(${cloud.color}, ${cloud.alpha})`);
        grad.addColorStop(0.45, `rgba(${cloud.color}, ${cloud.alpha * 0.4})`);
        grad.addColorStop(1, "rgba(0, 0, 0, 0)");

        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(ox, oy, cloud.radius, 0, Math.PI * 2);
        ctx.fill();
      });

      // 2. 绘制 3D 恒星与星辉
      for (let i = 0; i < stars.length; i++) {
        const star = stars[i];

        // 3D 深空向前漫游 (Warp Drift)
        star.z -= 0.22;
        if (star.z < 60) {
          star.z = 1000;
          star.x = (Math.random() - 0.5) * 2600;
          star.y = (Math.random() - 0.5) * 1800;
        }

        // 3D 透视投影公式
        const scale = fov / star.z;
        const px = (star.x - mouse.x * 420) * scale + cx;
        const py = (star.y - mouse.y * 300) * scale + cy;

        // 视口外裁剪
        if (px < -30 || px > width + 30 || py < -30 || py > height + 30) continue;

        // 真实呼吸闪烁计算
        star.twinklePhase += star.twinkleSpeed;
        const twinkle = 0.5 + Math.sin(star.twinklePhase) * 0.5; // 0 ~ 1
        const nearFade = Math.min(1, Math.max(0, (star.z - 60) / 90)); // 靠近镜头平滑淡出
        const depthFade = Math.min(1, Math.max(0.18, (1000 - star.z) / 800));
        const alpha = Math.min(0.96, Math.max(0.12, (0.35 + twinkle * 0.6) * depthFade * nearFade));
        
        // 动态半径计算
        const rawRadius = star.size * scale * (0.85 + twinkle * 0.35);
        const r = Math.min(rawRadius, star.hasSpike ? 2.8 : 2.0);

        // A. 绘制恒星外发光柔晕 (Halo)
        if (star.hasSpike || r > 1.1) {
          const haloR = r * (star.hasSpike ? 3.5 : 2.6);
          const haloGrad = ctx.createRadialGradient(px, py, 0, px, py, haloR);
          haloGrad.addColorStop(0, star.color + (alpha * 0.75) + ")");
          haloGrad.addColorStop(0.4, star.color + (alpha * 0.22) + ")");
          haloGrad.addColorStop(1, star.color + "0)");

          ctx.fillStyle = haloGrad;
          ctx.beginPath();
          ctx.arc(px, py, haloR, 0, Math.PI * 2);
          ctx.fill();
        }

        // B. 绘制璀璨十字衍射星辉 (Diffraction Spikes)
        if (star.hasSpike && alpha > 0.32) {
          const spikeLen = Math.min(r * 5.2, 16) * (0.7 + twinkle * 0.35); // 8px ~ 16px 晶莹星芒
          ctx.save();
          ctx.strokeStyle = star.color + (alpha * 0.7) + ")";
          ctx.lineWidth = 0.65;
          ctx.beginPath();
          // 水平光辉
          ctx.moveTo(px - spikeLen, py);
          ctx.lineTo(px + spikeLen, py);
          // 垂直光辉
          ctx.moveTo(px, py - spikeLen);
          ctx.lineTo(px, py + spikeLen);
          ctx.stroke();
          ctx.restore();
        }

        // C. 绘制恒星核心实体高亮点 (Core)
        ctx.fillStyle = star.color + alpha + ")";
        ctx.beginPath();
        ctx.arc(px, py, Math.max(0.5, r), 0, Math.PI * 2);
        ctx.fill();
      }

      animId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 pointer-events-none ${className}`}
      style={{ mixBlendMode: "screen" }}
    />
  );
};

export default CosmicCanvas;
